import { defineStore } from "pinia";
import axios from "axios";
import { configuratorIdStore } from "./configuratorIdStore";
import {
  BackDataFreeText,
  BackDataType,
  BuildIdValuesResponse,
  CurrentCoding,
  DataConfigOptions,
  InitialConfig,
  InitialModelConfig,
} from "./types";

type qrcodeType = {
  mode: string;
  size: number;
};

type ModelVarients = {
  model: string;
  dispayTitle: string;
  displayText: string;
};

export interface TypeObject {
  CodeType: string;
  CodeString?: string;
  FreeTexts?: BackDataFreeText[];
}

export interface ModelObject {
  TypeObject: TypeObject;
}

export interface buildIdModalSection {
  title: string;
  text: string;
  button: ButtonConfig;
}

export interface DataConfig {
  Description: string;
  Selected: boolean;
  Text: string;
  displayViewSwitch: boolean;
  showAdministratorWidget: boolean;
  Id: string | string[];
  mode: string;
  autoUpdateDrawings: boolean;
  loginName: string; // for login
  websessionId: string; // for login
  version: number; // for login
  displayDispatchInformation: boolean; // for detail
  isDeliveryDateExp: boolean; // for detail
  shippingDetailUrl: string; // for detail
  customerNumber: string; // for create
  environment: string; // for create id
  original_url: string; // for create id
  region: string; // for create id
  language: string; // for create id
  qrcode: qrcodeType; // for create id
  zIndex: number;
  thumbnailsUrl: string;
  thumbnailsJsonCacheTimeInSeconds: number;
  thumbnailsPlaceholderUrl: string;
  supportEmail: boolean;
  supportPhone: boolean;
  supportChat: boolean;
  data_action_buttons: string;
  modelVariants?: ModelVarients[];
  qty: number;
  maxQty: number;
  minQty: number;
  isPriceDetailsVisible: boolean;
  modelCodeObj?: ModelObject[];
  currentModelCodeObj?: ModelObject[];
  itemid?: string;
  langPath?: string;
  buildId: string;
  disableCustomerNo?: boolean;
  showPricing?: boolean;
  showOptionId?: boolean;
  considerSalesQuantities: boolean;
  displayDeviceController?: boolean;
  contentStringType?: string;
  hasTagNumbers?: boolean;
  stockOnHandVisible: boolean;
  planNumber: string;
  showDeliveryDateInbound: boolean; // false : Vsl. Versanddatum  true: Vsl. Lieferdatum
  displayGroupCriteriaChekbox: boolean; // to show Extended configuration
  hideAssemblies?: boolean;
  showExitWarning?: boolean;
  buildIdModalSection?: buildIdModalSection;
  noPriceAvailableText?: string;
  isLoggedIn?: boolean;
  promoSectionEnabled: boolean;
  promoSectionButton: string | number;
  linkTarget: string;
  promoSectionBannerImageUrl: string;
  downloadFormSnippet?: string;
  assetBaseUrl?: string;
  shellModels?: Array<string>;
  manualOrdercodeEdit?: boolean;
  hideOrmig: boolean;
  customerData?: customerDataArray;
}

export interface customerDataArray {
  city: string,
  country: string,
  email: string,
  fax: string,
  firstname: string,
  lastname: string,
  phone: string,
  street: string,
  title: string,
  userfirm: string,
  zip: string,
}

export interface Config {
  LiveEnvPath: string;
  buildIdPath: string;
  getBuildIdValues: string;
  optionGraphicSettings: string;
  mailSupport: string;
  hotlinePhone: string;
  qrURI: string;
  qrIdURI: string;
  welcomeMessage: string;
  company: string;
  code?: string;
  cadenasBaseUrl: string;
  externalItemNoRequestUrl?: string;
}

export interface AdditionalStoreState {
  init: boolean;
  singleProduct: boolean;
  showToggleMode: boolean;
  langValue: string;
  langApi: string;
  config: Config;
  dataConfig: DataConfig;
  modelConfig: InitialModelConfig;
  options: DataConfigOptions;
  isMobile: boolean;
  optionFile: string;
  countryISO?: string;
}

export interface LinkItem {
  Description?: string;
  Id?: string;
  Selected?: boolean;
  Text?: string;
}

export interface FavouriteChild {
  label: string;
  class: string;
  icon: string;
  callbackExternal: string;
  callbackData: string;
  disabledWhileIncomplete: boolean;
}

export interface Overwrites {
  conditions: {
    isComplete: boolean;
    hasValidTermination: boolean;
    deliveryCategories: number[];
  };
  values: FavouriteChild;
}

export type ButtonConfig = {
  class?: string;
  disabledIfNotFeasible?: boolean;
  disabledWhileIncomplete?: boolean;
  hiddenForNonEvaluatingModels?: boolean;
  showSeparatorAbove?: boolean;
  icon?: string;
  label?: string;
  callbackExternal?: string;
  callbackInternal?: string;
  callbackData?: string;
  isMenu?: boolean;
  childs?: Array<FavouriteChild>;
  pregenerateBuildId?: boolean;
  forceGetResults?: boolean;
  overwrites?: Overwrites;
  disabled?: boolean;
};

const additionalDefaultState: AdditionalStoreState = {
  init: false,
  singleProduct: false,
  showToggleMode: false,
  langValue: "en-gb",
  langApi: "en-gb",
  countryISO: "",
  config: {
    LiveEnvPath: "https://portal.wika.com/app3/de11/portal-prod/",
    buildIdPath: "https://wika.build/api/v1/id/create",
    getBuildIdValues: "https://wika.build/api/v1/id/",
    optionGraphicSettings:
      "https://portal.wika.com/app3/global/configurator/gfx/",
    mailSupport: "info@wika.de",
    hotlinePhone: "0800 123 400 500",
    qrURI: "https://wika.build/qr/",
    qrIdURI: "https://wika.build/id/",
    company: "WIKA group",
    welcomeMessage: "",
    cadenasBaseUrl: "https://webapi.qa.partcommunity.com",
    externalItemNoRequestUrl: undefined,
  },
  dataConfig: {
    langPath: "/",
    buildId: "",
    Description: "",
    Selected: false,
    Text: "",
    Id: "",
    mode: "",
    isLoggedIn: false,
    autoUpdateDrawings: false,
    displayDispatchInformation: true,
    showDeliveryDateInbound: false,
    isDeliveryDateExp: true,
    displayViewSwitch: false,
    shippingDetailUrl: "",
    customerNumber: "",
    loginName: "",
    websessionId: "",
    version: 2,
    environment: "",
    original_url: "",
    region: "",
    language: "",
    showAdministratorWidget: false,
    qrcode: {
      mode: "",
      size: 400,
    },
    zIndex: 3000,
    qty: 1,
    maxQty: 99,
    minQty: 0,
    isPriceDetailsVisible: true,
    thumbnailsUrl:
      "https://www.wika.com/configurator_product_thumbnails_en.json",
    thumbnailsJsonCacheTimeInSeconds: 86400,
    thumbnailsPlaceholderUrl: "",
    supportChat: true,
    supportEmail: true,
    supportPhone: true,
    data_action_buttons: "{}",
    disableCustomerNo: true,
    showPricing: true,
    showOptionId: false,
    considerSalesQuantities: false,
    modelCodeObj: [],
    currentModelCodeObj: [],
    displayDeviceController: false,
    contentStringType: "",
    hasTagNumbers: false,
    stockOnHandVisible: false,
    planNumber: "",
    displayGroupCriteriaChekbox: false,
    hideAssemblies: false,
    showExitWarning: true,
    promoSectionEnabled: false,
    promoSectionButton: "",
    linkTarget: "",
    promoSectionBannerImageUrl: "",
    assetBaseUrl: "",
    shellModels: [],
    hideOrmig: false,
  },
  modelConfig: {
    type: "info",
    allowClose: false,
    content: "This is some content",
  },
  options: {},
  isMobile: false,
  optionFile: "",
};

export const useAdditionalAttrStore = defineStore("AdditionalAttrStore", {
  state: () => additionalDefaultState,
  getters: {
    isInit: (state) => state.init,
    getModeValue: (state) => state.dataConfig.mode,
    getlangValue: (state) => state.langValue,
    getLangApi: (state) => state.langApi,
    getLoginVersion: (state) => state.dataConfig.version,
    getDataConfig: (state) => {
      return state.dataConfig;
    },
    getOptions: (state) => {
      return state.options;
    },
    getLoginName(state) {
      if (state.dataConfig.loginName) {
        return state.dataConfig.loginName;
      }
    },
    getIsMobile: (state) => {
      return state.isMobile;
    },
    getOptionFile: (state) => {
      return state.optionFile;
    },
    getIds(state): string[] {
      if (!state.dataConfig.Id) {
        return [];
      }
      if (typeof state.dataConfig.Id === "string") {
        return [state.dataConfig.Id];
      } else {
        return state.dataConfig.Id;
      }
    },
    getCommunicationConfig: (state) => {
      return state.config;
    },
    getshowExitWarning: (state) => {
      return state.dataConfig.showExitWarning;
    },
    getNoPriceAvailableText: (state) => {
      return state.dataConfig.noPriceAvailableText;
    },
    getcountryISO: (state) => {
      return state.countryISO;
    },
  },
  actions: {
    initConfig(config: InitialConfig) {
      if (Object.keys(config).length) {
        const dataConfig = config["data-config"];

        this.setCommmunicationConfigs(config.config);
        // set action-buttons
        // if (typeof config["data-action-buttons"] !== "undefined") {
        //   this.setOptionFile(config["data-action-buttons"]);
        // }

        if (!dataConfig?.itemid) {
          this.dataConfig.itemid = "";
          this.dataConfig.modelCodeObj = [];
        }

        if (config["data-action-buttons"]) {
          this.setOptions(config["data-action-buttons"]);
        }
        // set config
        if (dataConfig) {
          this.setConfigs(dataConfig);
          this.singleProduct = !!(
            typeof dataConfig.Id !== "object" && dataConfig.Id
          );
        }
        // set lang
        if (config.lng) {
          this.setLang(config.lng);
        } else {
          this.setLang("de");
        }
        if (config.langAPI) {
          this.langApi = config.langAPI;
        } else {
          this.langApi = "en-gb";
        }

        if (dataConfig) {
          this.dataConfig.environment = dataConfig.environment;
        }
        this.init = true;
      }
    },
    initModelConfig(modelConfig: InitialModelConfig) {
      this.modelConfig = modelConfig;
    },
    setLang(langValue: string) {
      this.langValue = langValue;
      this.dataConfig.language = langValue;
    },
    setApiLang(langApi: string) {
      this.langApi = langApi;
    },
    async setCommmunicationConfigs(config: Config) {
      if (config) {
        this.config = Object.assign({}, this.config, config);
      }
    },
    async setConfigs(dataConfig: DataConfig) {
      if (dataConfig) {
        this.dataConfig = Object.assign({}, this.dataConfig, dataConfig);
        const cis = configuratorIdStore();
        cis.setConditionData(this.dataConfig);
      }
    },
    setConfigByItem(item: LinkItem) {
      this.dataConfig.Id = item.Id ?? "";
      this.dataConfig.Selected = !!item.Selected;
    },
    setConfigWithCodeQuery(
      id: string,
      configurations: BuildIdValuesResponse["configurations"]
    ) {
      if (id) {
        this.dataConfig.Id = id;
      }
      if (configurations.length) {
        const cis = configuratorIdStore();
        const tempData: BackDataType[] = [];
        configurations.forEach((configuration) => {
          const tempFreeTexts: BackDataFreeText[] = [];
          if (cis.backItem === "") {
            cis.setBackItem(configuration.modelcode_type);
          }
          if (configuration.configurationFreeTexts) {
            configuration.configurationFreeTexts.forEach((freeText) => {
              tempFreeTexts.push({
                Criteria: freeText.criterion,
                Text: freeText.text,
              });
            });
          }
          tempData.push({
            TypeObject: {
              CodeType: configuration.modelcode_type,
              CodeString: configuration.modelcode,
              FreeTexts: tempFreeTexts,
            },
          });
        });
        cis.setBackData(tempData);
      }
    },
    setOptions(options: DataConfigOptions) {
      if (options) {
        this.options = options;
      }
    },
    setModelCodes(modelCodes: ModelVarients[]) {
      if (modelCodes && modelCodes.length > 1) {
        this.showToggleMode = true;
      }
      this.dataConfig.modelVariants = modelCodes;
    },
    setIsMobile(isMobile: boolean) {
      this.isMobile = isMobile;
    },
    setOptionFile(file: string) {
      this.optionFile = file;
    },
    hideToggleMode() {
      this.showToggleMode = false;
    },
    async getOptionData(): Promise<DataConfigOptions> {
      if (this.optionFile !== "" && Object.keys(this.options).length === 0) {
        try {
          const responseOptions = await axios.get(this.optionFile);
          if (responseOptions.data !== undefined) {
            this.setOptions(responseOptions.data);
          }
        } catch (error) {
          console.log(error);
        }
      }
      return this.options;
    },
    setDisplayViewSwitch(displayViewSwitch: boolean) {
      this.dataConfig.displayViewSwitch = displayViewSwitch;
    },
    setShowAdministratorWidget(showAdministratorWidget: boolean) {
      this.dataConfig.showAdministratorWidget = showAdministratorWidget;
    },
    setLangPath(langPath: string) {
      this.dataConfig.langPath = langPath;
    },
    setEnviroment(environment: string) {
      this.dataConfig.environment = environment;
    },
    setdisplayDeviceController(displayDeviceController: boolean | undefined) {
      this.dataConfig.displayDeviceController = displayDeviceController;
    },
    setShowPricing(showPricing: boolean | undefined) {
      this.dataConfig.showPricing = showPricing;
    },
    setIsPriceDetailsVisible(isPriceDetailsVisible: boolean) {
      this.dataConfig.isPriceDetailsVisible = isPriceDetailsVisible;
    },
    setIsDeliveryDateExp(isDeliveryDateExp: boolean) {
      this.dataConfig.isDeliveryDateExp = isDeliveryDateExp;
    },
    setDisplayDispatchInformation(displayDispatchInformation: boolean) {
      this.dataConfig.displayDispatchInformation = displayDispatchInformation;
    },
    setautoUpdateDrawings(autoUpdateDrawings: boolean) {
      this.dataConfig.autoUpdateDrawings = autoUpdateDrawings;
    },
    setStockOnHandVisible(stockOnHandVisible: boolean) {
      this.dataConfig.stockOnHandVisible = stockOnHandVisible;
    },
    sethideAssemblies(hideAssemblies: boolean) {
      this.dataConfig.hideAssemblies = hideAssemblies;
    },
    setshowOptionId(showOptionId: boolean) {
      this.dataConfig.showOptionId = showOptionId;
    },
    setContentStringType(contentStringType: string | undefined) {
      this.dataConfig.contentStringType = contentStringType;
    },
    async setCurrentModelCodeObj(selectProduct: CurrentCoding) {
      this.clearCurrentModelCodeObj();

      if (this.dataConfig.currentModelCodeObj?.length) {
        const cmc = this.dataConfig.currentModelCodeObj;
        cmc.forEach((modelObject, k) => {
          if (modelObject.TypeObject.CodeType === selectProduct.CodeTypeId) {
            cmc[k].TypeObject.CodeString = selectProduct.ContentStringPublic;
          }
        });
        this.dataConfig.currentModelCodeObj = cmc;
      } else {
        this.dataConfig.currentModelCodeObj?.push({
          TypeObject: {
            CodeType: selectProduct.CodeTypeId,
            CodeString: selectProduct.ContentStringPublic,
          },
        });
      }
    },
    async setCurrentModelCodeObjs(Item: ModelObject[]) {
      this.dataConfig.modelCodeObj = Item;
    },
    clearCurrentModelCodeObj() {
      this.dataConfig.currentModelCodeObj = [];
    },
    clearId() {
      this.dataConfig.Id = "";
    },
    setshowExitWarning(showExitWarning: boolean) {
      this.dataConfig.showExitWarning = showExitWarning;
    },
    setexternalItemNoRequestUrl(externalItemNoRequestUrl: string) {
      this.config.externalItemNoRequestUrl = externalItemNoRequestUrl;
    },
    setNoPriceAvailableText(noPriceAvailableText: string) {
      this.dataConfig.noPriceAvailableText = noPriceAvailableText;
    },
    setcountryISOCode(countryISOCode: string) {
      this.countryISO = countryISOCode;
    },
    setAssetBaseUrl(assetBaseUrl: string | undefined) {
      this.dataConfig.assetBaseUrl = assetBaseUrl;
    },
    setShellModels(shellModels: Array<string>) {
      this.dataConfig.shellModels = shellModels;
    },
    setLoginName(loginName: string) {
      this.dataConfig.loginName = loginName;
    },
  },
});
